import React, { useState, useCallback, useId, useRef, useEffect } from "react";
import { cn } from "../../styles";
import { ThemeColor } from "../../props";

export interface TabProps {
  label: React.ReactNode;
  value: string;
  disabled?: boolean;
}

export interface TabBarProps {
  value?: string;
  onChange?: (value: string) => void;
  tabs: TabProps[];
  color?: ThemeColor;
  className?: string;
  tabClassName?: string;
  indicatorClassName?: string;
  disabled?: boolean;
  fullWidth?: boolean;
}

export interface TabPanelProps {
  value: string;
  activeValue: string;
  children: React.ReactNode;
  className?: string;
}

export const TabPanel = ({
  value,
  activeValue,
  children,
  className,
}: TabPanelProps) => {
  if (value !== activeValue) return null;

  return (
    <div role="tabpanel" className={className}>
      {children}
    </div>
  );
};

TabPanel.displayName = "TabBar.Panel";

const colorStyles: Record<ThemeColor, { active: string; indicator: string }> = {
  primary: {
    active: "text-primary-600 dark:text-primary-400",
    indicator: "bg-primary-500 dark:bg-primary-400",
  },
  secondary: {
    active: "text-secondary-600 dark:text-secondary-400",
    indicator: "bg-secondary-500 dark:bg-secondary-400",
  },
  gray: {
    active: "text-front-700 dark:text-front-300",
    indicator: "bg-front-500 dark:bg-front-400",
  },
  error: {
    active: "text-error-600 dark:text-error-400",
    indicator: "bg-error-500 dark:bg-error-400",
  },
  warning: {
    active: "text-warning-600 dark:text-warning-400",
    indicator: "bg-warning-500 dark:bg-warning-400",
  },
  success: {
    active: "text-success-600 dark:text-success-400",
    indicator: "bg-success-500 dark:bg-success-400",
  },
  transparent: {
    active: "text-front-700 dark:text-front-300",
    indicator: "bg-front-400 dark:bg-front-500",
  },
  none: {
    active: "",
    indicator: "",
  },
  danger: {
    active: "text-error-600 dark:text-error-400",
    indicator: "bg-error-500 dark:bg-error-400",
  },
  alert: {
    active: "text-warning-600 dark:text-warning-400",
    indicator: "bg-warning-500 dark:bg-warning-400",
  },
  done: {
    active: "text-success-600 dark:text-success-400",
    indicator: "bg-success-500 dark:bg-success-400",
  },
};

export const TabBar = ({
  value,
  onChange,
  tabs,
  color = ThemeColor.primary,
  className,
  tabClassName,
  indicatorClassName,
  disabled = false,
  fullWidth = false,
}: TabBarProps) => {
  const id = useId();
  const [internalValue, setInternalValue] = useState(tabs[0]?.value);
  const activeValue = value ?? internalValue;
  const tabRefs = useRef<(HTMLButtonElement | null)[]>([]);
  const [indicatorStyle, setIndicatorStyle] = useState<React.CSSProperties>({});

  const handleChange = useCallback(
    (newValue: string) => {
      if (disabled) return;
      setInternalValue(newValue);
      onChange?.(newValue);
    },
    [disabled, onChange],
  );

  const activeIndex = tabs.findIndex((tab) => tab.value === activeValue);
  const styles = colorStyles[color];

  const updateIndicator = useCallback(() => {
    if (fullWidth) {
      setIndicatorStyle({
        left: `${(activeIndex / tabs.length) * 100}%`,
        width: `${100 / tabs.length}%`,
      });
      return;
    }
    const activeTab = tabRefs.current[activeIndex];
    if (!activeTab) return;
    setIndicatorStyle({
      left: `${activeTab.offsetLeft}px`,
      width: `${activeTab.offsetWidth}px`,
    });
  }, [activeIndex, tabs.length, fullWidth]);

  useEffect(() => {
    updateIndicator();
  }, [updateIndicator]);

  // Labels can grow after first paint (async localization, badges), which would
  // leave the indicator stuck at its initial, too-narrow measurement.
  useEffect(() => {
    if (fullWidth || typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(updateIndicator);
    tabRefs.current.forEach((tab) => tab && observer.observe(tab));
    return () => observer.disconnect();
  }, [fullWidth, updateIndicator, tabs.length]);

  return (
    <div
      className={cn("relative", className)}
      role="tablist"
      aria-orientation="horizontal"
    >
      <div className={cn("flex", fullWidth && "w-full")}>
        {tabs.map((tab, index) => {
          const isActive = tab.value === activeValue;
          const isDisabled = disabled || tab.disabled;

          return (
            <button
              key={tab.value}
              ref={(el) => (tabRefs.current[index] = el)}
              id={`${id}-tab-${index}`}
              role="tab"
              type="button"
              aria-selected={isActive}
              aria-controls={`${id}-panel-${index}`}
              tabIndex={isActive ? 0 : -1}
              disabled={isDisabled}
              onClick={() => handleChange(tab.value)}
              className={cn(
                "px-8 py-4 text-sm font-medium relative",
                "hover:bg-back-100 dark:hover:bg-back-800",
                "focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500",
                fullWidth && "flex-1",
                isActive ? styles.active : "text-front-500 dark:text-front-400",
                isDisabled && "opacity-50 cursor-not-allowed",
                tabClassName,
              )}
            >
              {tab.label}
            </button>
          );
        })}
      </div>
      <div
        className={cn(
          "absolute bottom-0 h-0.5 transition-all duration-200",
          styles.indicator,
          indicatorClassName,
        )}
        style={indicatorStyle}
      />
    </div>
  );
};

TabBar.displayName = "TabBar";

TabBar.Panel = TabPanel;
